import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import config from 'config';
import { track } from 'analytics/posthog';

export const ADMIN_AUTH_TOKEN_HEADER = 'admin-authorization-token';
export const ADMIN_API_BASE_URL = '/api/v1/admin';

const TRACKED_API_FAILURE_PATHS = new Set([
  '/api/v1/tv-command',
  '/api/v1/pod-command',
  '/api/v1/configure-vpn',
  '/v1/on-demand/setup-creative-self-serve',
  '/v1/on-demand/setup-creative-adsure',
  '/v1/on-demand/activate-placement',
  '/api/creative-testing/v1/retest',
]);

const getRequestUrl = (args: string | FetchArgs) =>
  typeof args === 'string' ? args : args.url;

const getRequestMethod = (args: string | FetchArgs) =>
  typeof args === 'string' ? 'GET' : (args.method || 'GET').toUpperCase();

const getTrackedEndpointPath = (args: string | FetchArgs) => {
  const requestUrl = getRequestUrl(args);

  try {
    return new URL(requestUrl, config.serverUrl).pathname;
  } catch {
    return undefined;
  }
};

const getApiErrorMessage = (error: FetchBaseQueryError) => {
  if ('error' in error && error.error) return error.error;

  if ('data' in error) {
    const data = error.data as any;
    return String(data?.error || data?.message || 'unknown');
  }

  return 'unknown';
};

export const prepareAuthHeaders = (headers: Headers) => {
  if (!headers.has('authorization-token')) {
    const token = localStorage.getItem('authorization-token') || '';
    if (token !== '') headers.set('authorization-token', token);
  }
  return headers;
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.serverUrl,
  prepareHeaders: prepareAuthHeaders,
});

const baseQueryWithFailureTracking: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let requestArgs = args;
  const requestUrl = getRequestUrl(args);
  const permissionToken = localStorage.getItem('permission-token') || '';
  if (permissionToken) {
    const request = typeof args === 'string' ? { url: args } : args;
    const headers = new Headers(
      request.headers as unknown as HeadersInit,
    );
    headers.set('permission-token', permissionToken);
    requestArgs = { ...request, headers };
  }

  if (
    requestUrl?.startsWith('/api/creative-testing/v1/list') ||
    requestUrl?.includes('on-demand')
  ) {
    const umsId = localStorage.getItem('umsId') || '';
    const userObj = localStorage.getItem('user') || '';
    let name = '';
    try {
      name = JSON.parse(userObj)?.name || '';
    } catch {
      // Ignore malformed locally stored user data.
    }
    const request =
      typeof requestArgs === 'string' ? { url: requestArgs } : requestArgs;
    const headers = new Headers(
      request.headers as unknown as HeadersInit,
    );
    if (umsId) headers.set('umsId', umsId);
    if (name) headers.set('name', name);
    requestArgs = { ...request, headers };
  }

  const result = await rawBaseQuery(requestArgs, api, extraOptions);

  if (result.error) {
    const endpointPath = getTrackedEndpointPath(args);

    if (endpointPath && TRACKED_API_FAILURE_PATHS.has(endpointPath)) {
      try {
        track('api request failed', {
          endpoint: endpointPath,
          method: getRequestMethod(args),
          status_code:
            typeof result.error.status === 'number'
              ? result.error.status
              : undefined,
          error_message: getApiErrorMessage(result.error),
          surface: 'api',
        });
      } catch {
        // Never let analytics side effects interfere with API behavior.
      }
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithFailureTracking,
  tagTypes: [
    'remoteLayout',
    'remotePodMapping',
    'creativeTesting',
    'tvCommands',
    'userGuide',
    'userFeedback',
    'adminAdsureMonitoring',
    'adminApplicationCredentials',
    'adminPods',
    'adminPodActionLogs',
  ],
  endpoints: builder => ({
    podList: builder.query<any, string | void>({
      query: pod => ({
        url: '/api/v1/pod-list',
        method: 'get',
        params: pod ? { pod } : undefined,
      }),
    }),
    init: builder.mutation({
      query: ({ pod }) => ({
        url: `/api/v1/init`,
        method: 'post',
        body: { pod },
      }),
    }),
    sendCommand: builder.mutation({
      query: ({ command, pod }) => ({
        url: `/api/v1/tv-command`,
        method: 'post',
        body: { command, pod },
      }),
    }),
    restartPuppeteer: builder.mutation({
      query: ({ pod }) => ({
        url: `/api/v1/pod-command`,
        method: 'post',
        body: {
          pod,
          commandId: 'pm2-restart',
          args: { processId: '0' },
        },
      }),
    }),
    getScreenshot: builder.mutation({
      query: ({ pod }) => ({
        url: `/api/v1/screenshot?pod=${pod}`,
        method: 'get',
      }),
    }),
    remoteRefresh: builder.mutation({
      query: ({ pod }) => ({
        url: `/api/v1/video-reload?pod=${pod}`,
        method: 'get',
      }),
    }),
    getStreamList: builder.query({
      query: () => ({
        url: '/stream/api/streams',
        method: 'get',
      }),
      transformResponse: (response: any) => {
        return Object.keys(response.live);
      },
    }),
    getVpnServers: builder.mutation({
      query: ({ vpnProvider, countryCode }) => ({
        url: `/api/v1/vpn-servers?vpn_provider=${vpnProvider}&country_code=${countryCode}`,
        method: 'get',
      }),
    }),
    getVpnCountries: builder.mutation({
      query: ({ vpnProvider }) => ({
        url: `/api/v1/vpn-countries?vpn_provider=${vpnProvider}`,
        method: 'get',
      }),
    }),
    getVpnConfiguration: builder.mutation({
      query: ({ pod }) => ({
        url: `/api/v1/configure-vpn?pod=${pod}`,
        method: 'get',
      }),
    }),
    setVpnConfiguration: builder.mutation({
      query: body => ({
        url: '/api/v1/configure-vpn',
        method: 'post',
        body,
      }),
    }),
    getPeerId: builder.query({
      query: pod => ({
        url: `/api/v1/peer-id?pod=${pod}`,
        method: 'get',
      }),
    }),
    getIceServers: builder.query({
      query: () => ({
        url: config.ICE_SERVER_URL,
        method: 'get',
      }),
    }),
    getConfig: builder.query<
      {
        enableMagicRemote: boolean;
        mosaicUrl?: string | null;
        mosaicStagingUrl?: string | null;
        umsUrl?: string | null;
        creativeServerUrl?: string | null;
        monitoringSources?: string[];
        googleClientId?: string | null;
        posthogToken?: string;
        posthogHost?: string;
        cdnUrl: string;
      },
      void
    >({
      query: () => ({
        url: '/api/v1/config',
        method: 'get',
      }),
      keepUnusedDataFor: 0, // Don't use stale cache - always refetch fresh config
    }),
  }),
});

export const {
  useGetScreenshotMutation,
  useGetStreamListQuery,
  useGetVpnServersMutation,
  useGetVpnCountriesMutation,
  useGetVpnConfigurationMutation,
  useSetVpnConfigurationMutation,
  useInitMutation,
  useSendCommandMutation,
  useRestartPuppeteerMutation,
  usePodListQuery,
  useGetPeerIdQuery,
  useRemoteRefreshMutation,
  useGetIceServersQuery,
  useGetConfigQuery,
} = api;

export default api;
